// SecureScan365 walk-through content registry — v3 (branched).
// Internal identifiers keep upgrade/wizard naming; customer copy uses the
// SecureScan365 Prerequisites label.
//
// Sources (priority order, per the v3 build instruction):
//   1. docs/plans/securescan365-walkthrough-v3-draft.md — structure spec
//   2. "Patriot SecureScan365 - New Client Onboarding - v2.docx" — step
//      copy, FAQ, troubleshooting, roles table (reviewed; used nearly
//      verbatim)
//   3. "Patriot SecureScan365 - Existing Client Onboarding.docx" — the
//      existing-account variant
// Two typo fixes carried per instruction: "temporary service principle"
// → principal; "will us to manually access" → "will allow us to manually
// access". The Fabric (Free) license step is deleted (no license of any
// kind is required now); its two troubleshooting rows and the
// usage-location sub-step (which existed only for the license) are gone.
// Open questions from the spec are DRAFT placeholders in MOCK_REGISTRY —
// none are answered here.

// CUSTOMER-LIVE (operator sign-off 2026-08-05): both surfaces opened
// after the content sweep completed — every draft question answered,
// withdrawn, or signed off; zero bracket markers in rendered content.
// The 2026-08-04 hold is fulfilled, not overridden. Delivery-mode
// suppression is unaffected (both surfaces still non-render during
// presentations).
export const SHOW_UPGRADE_TO_CUSTOMERS = true;
// Security Scan card (check setup / run scan) — same sign-off. A legacy
// customer sees the card LOCKED with the unlock hint, never hidden.
export const SHOW_SCAN_CARD_TO_CUSTOMERS = true;
export const SURFACE_TITLE = "Get Ready for SecureScan365";

// Entry/chrome copy remains draft until Amber voice + Joe sign-off; the
// STEP copy below comes from the reviewed onboarding documents.
export const ENTRY_COPY = {
  // Timing is stated PER PATH (on the Step-0 choice cards) — the access
  // call exists only on the new-account path, so there is no global
  // duration line anymore.
  securityBullets: [
    { title: "Read-only", text: "The account holds the Global Reader role and read-only Microsoft Graph permissions. It cannot change anything in your tenant." },
    // Deliberately narrowed to the scan account — the wider claim read
    // as if passwordless applied to the customer's users.
    { title: "Passwordless", text: "No password is set, shared, or transmitted for the SecureScan365 scan account." },
    { title: "Scoped and revocable", text: "Everything is tied to a single account inside a single security group that you own. You can disable or remove access at any time." },
  ],
  onSecureScan365: "Your assessments run on SecureScan365. No action needed. The prerequisites guide below is available for reference.",
  legacyBanner: "Your assessments currently run on our legacy scan tool. SecureScan365 is Patriot's new collection technology and is replacing the legacy method, which is being deprecated. New security settings are added to SecureScan365 only — the legacy tool no longer receives new coverage. Completing the prerequisites below gets your tenant ready.",
  unclassified: "Contact your Patriot team to confirm your assessment configuration.",
  getStartedLabel: "Get started",
  faqLabel: "Security questions & answers",
};

export const WIZARD_COPY = {
  surfaceName: "SecureScan365 Prerequisites",
  // draftBanner removed 2026-08-05 with the customer launch — the DRAFT
  // banner plumbing is gone; HAS_DRAFT_MOCKS (now false) remains the
  // registry's truth signal for tests.
  intro: "SecureScan365 is Patriot's collection technology for your SecureShield365 security posture assessment. It signs in to your Microsoft 365 tenant with a read-only account you create and control, gathers your current configuration settings, and returns the results to Patriot for scoring and review in your SecureShield365 Client Portal.",
  // Existing-client doc intro line (typo fixed: principle → principal).
  noServicePrincipalLine: "This no longer needs a temporary service principal to be created like the previous scanning technology used.",
  // "completion" and "restartLabel" removed 2026-08-04 with the terminal
  // completed-mode branch — booking assistance never terminates the wizard now;
  // the per-path completionConfirmation strings live on WIZARD_PATHS.
  resumeLabel: "Resume",
  collapseLabel: "Collapse",
  startOverLabel: "Start over",
  startOverMessage: "Your checklist progress and account choice will reset. Tickets already filed are not affected.",
  referenceFaqLabel: "Security Q&A",
  referenceTroubleshootingLabel: "Troubleshooting",
  referenceRolesLabel: "Roles & access needed",
  // ONE help entry point (operator decision 2026-08-04) — replaces the
  // separate "I'm stuck" and "Prefer to do this together?" links.
  needHelpLabel: "Need help?",
  needHelpChoiceTitle: "How would you like Patriot to help?",
  needHelpTicketAction: "Submit a support ticket",
  needHelpMeetingAction: "Book a meeting",
  needHelpConfirmation: "Thanks — your request has been sent to Patriot. Our team will follow up.",
};

// ── Need-help form (single help entry point, 2026-08-04) ─────────────
// Reason keys are the STRUCTURED values the backend routes and
// prioritizes on (wizard_help_reason in the create_ticket body; carried
// to Freshservice as a wizard-help:<key> tag). Keys are stable API
// identifiers — display labels are free to change, keys are not.
export const HELP_REASONS = [
  { key: "stuck_on_step", label: "I'm stuck on a step" },
  { key: "question_before_starting", label: "I have a question before starting" },
  { key: "request_walkthrough", label: "I'd like to do this together on a walk-through call" },
];

export const HELP_SUBJECT = "SecureScan365 Prerequisites — help requested";

// ── Step 0 — which account will we use? (spec §Step 0) ───────────────
export const PATH_CHOICE_COPY = {
  heading: "Which account will we use?",
  intro: "Before we start, one question that decides how much setup is involved.",
  existing: {
    label: "Use an account Patriot already has",
    tag: "Recommended — shorter setup",
    body: "If Patriot already has an account in your tenant from previous assessments, we can reuse it. That account is already created, already excluded from the Conditional Access policies that would otherwise block setup, and already known to your team. Most of the steps below disappear. When you’re done, book a short onboarding meeting so Patriot can register the passkey and validate the setup.",
    upnLabel: "Which Patriot account should we use?",
    // Never name a Patriot person or internal tooling here — the wizard
    // asks which account exists, it never assumes.
    upnHelper: "Enter the account exactly as it appears in your tenant — it should match what Patriot has on file. If you are not sure, use \"I'm not sure\" and we will look it up and confirm.",
    unsureLabel: "I'm not sure — Patriot, please look it up",
  },
  fresh: {
    label: "Create a new account for SecureScan365",
    tag: "30+ minutes, plus a scheduled call",
    body: "A fresh, cloud-only account dedicated to the assessment. The setup steps take 30 or more minutes, and the final registration requires a short scheduled call with Patriot.",
  },
};

// Downloadable per-path setup guides — real assets in
// frontend/public/guides/, deliberately NOT draft-flagged. Offered only
// AFTER the path choice, and only the guide matching the chosen path:
// an offline copy for a colleague without portal access. (The wizard
// itself remains the canonical prerequisites guide.)
export const GUIDE_PDFS = {
  existing: {
    label: "Download the Existing-Account setup guide (PDF)",
    href: "/guides/SecureScan365-Setup-Guide-Existing-Account.pdf",
  },
  new: {
    label: "Download the New-Account setup guide (PDF)",
    href: "/guides/SecureScan365-Setup-Guide-New-Account.pdf",
  },
};
export const GUIDE_PDF_NOTE =
  "An offline copy of these steps you can share with a colleague who doesn't have portal access.";

// ── Paths ─────────────────────────────────────────────────────────────
// Path 1 (existing Patriot account): 9 steps. Path 2 (new account): 12.
// Step ids are never renumbered (stuck tickets and saved progress key on
// them): E5 (Conditional Access) is RETIRED permanently — its content
// moved to Troubleshooting; E9 (Restrict Graph CLI Tools, shared with
// N8) slots after E3 mirroring N7→N8; E10/N12 are the "What you can
// remove" cleanup step, locked until the first successful scan.
// completionStepId is where the "tell Patriot / schedule the call" CTA
// lives; its gate is every step BEFORE it done/skipped. Steps after it
// happen with or after Patriot (register, swap roles, keep active).
// Booking the CTA's assistance meeting must NEVER complete setup: the
// customer stays on the step and sees completionConfirmation inline until
// the first successful SecureScan365 scan flips the scan method.
export const WIZARD_PATHS = {
  existing: {
    key: "existing",
    label: "Existing Patriot account",
    steps: ["E1", "E2", "E3", "E9", "E4", "E6", "E7", "E8", "E10"],
    completionStepId: "E6",
    completionLabel: "Tell Patriot these steps are complete",
    completionHelper: "Book a short onboarding meeting when the steps above are complete. Patriot will register the passkey using the account’s existing stored credentials and run the readiness checks with you.",
    completionConfirmation: "Your SecureScan365 onboarding meeting is booked.",
  },
  new: {
    key: "new",
    label: "New account",
    steps: ["N1", "N2", "N3", "N4", "N5", "N6", "N7", "N8", "N9", "N10", "N11", "N12"],
    completionStepId: "N9",
    completionLabel: "Schedule your setup call",
    completionHelper: "Approximately 45 minutes. You create the Temporary Access Pass live on the call, and we register the passkey and validate the setup together.",
    completionConfirmation: "Your SecureScan365 onboarding meeting is booked.",
  },
};

export function pathSteps(pathKey) {
  return WIZARD_PATHS[pathKey]?.steps || [];
}

export function emptyUpgradeChecklist(pathKey) {
  return Object.fromEntries(pathSteps(pathKey).map(key => [key, "pending"]));
}

// Keys that must be done/skipped before the completion CTA unlocks —
// every step before the completion step.
export function completionGateKeys(pathKey) {
  const path = WIZARD_PATHS[pathKey];
  if (!path) return [];
  const at = path.steps.indexOf(path.completionStepId);
  return at <= 0 ? [] : path.steps.slice(0, at);
}

export function completionReady(pathKey, checklist = {}) {
  const keys = completionGateKeys(pathKey);
  return keys.length > 0 && keys.every(
    key => checklist[key] === "done" || checklist[key] === "skipped"
  );
}

// ── Shared step fragments (identical in both paths) ──────────────────
const GROUP_STEP = {
  title: "Create the security group",
  instructions: [
    "This group is the scoping boundary for everything that follows — the passkey profile and the Graph access restriction target this one group, so nothing you enable applies to the rest of your tenant. It is also how the Global Reader role is held.",
    "Go to Entra → Groups → New group.",
    "Group type: Security. Recommended name: Patriot Scan 365. Add a description such as \"Scoping group for Patriot SecureScan365 read-only assessment account.\" If you have your own naming convention for groups, that is fine — use it.",
    "\"Microsoft Entra roles can be assigned to the group\": Yes. This option can ONLY be set when the group is created — if the group was created with it off, delete the group and recreate it with the option on.",
    "If you cannot enable that option, this does not block you: assign Global Reader directly to the scan account instead (see the Global Reader step).",
    "Membership type: Assigned.",
    "Add the scan account as the only member, then create the group.",
  ],
  images: [
    { src: "/wizard/e2-n2-group-create-roles-assignable.png" },
    { src: "/wizard/e2-n2-group-global-reader-row.png" },
  ],
  tip: null,
  troubleshooting: null,
};

const PASSKEY_STEP = {
  title: "Enable passkeys and create the Patriot passkey profile",
  instructions: [
    "The scan account authenticates with a device-bound passkey that Patriot stores in a locked-down Azure Key Vault. This step enables the passkey method for only the Patriot security group and restricts which passkey provider can be registered — passkeys are not being opened up to your wider user base.",
    "Go to Entra → Protection → Authentication methods → Policies and select Passkey (FIDO2).",
    "If the method is currently disabled, switch Enable on and save first — the Configure tab will not accept a new profile until the method has been enabled and saved once.",
    "On the Configure tab, select Add profile: Name: Patriot SecureScan365 Passkey Profile; Enforce attestation: Off (Key Vault–stored passkeys do not use FIDO Alliance attestation); Passkey type: Device-bound.",
    "Allow self-service setup: must be Enabled. This is a required setting — registration fails without it.",
    // All three AAGUIDs confirmed required (Pablo follow-up, 2026-08-02).
    // Windows Hello and Microsoft Authenticator are picked from the Add
    // AAGUID dialog's built-in options — never typed — so only the Key
    // Vault value gets a copy control.
    "Target specific AAGUIDs: checked, behavior Allow, then add all three: Windows Hello and Microsoft Authenticator (selected from the Add AAGUID dialog's built-in options), plus Patriot's Key Vault AAGUID:",
    { copy: { label: "Patriot Key Vault AAGUID", value: "33867143-325b-48d0-adfc-e7ae975fe068" } },
    // The "used to complete your Azure Security Assessment and Security
    // Health Checks" sentence was CUT 2026-08-04 (operator): it
    // contradicted the Q-7 FAQ answer (walkthrough is SecureScan365
    // only; other services are set up separately) and asserted coverage
    // that may not hold — those services can need different permissions
    // under separate accounts.
    "Windows Hello and Microsoft Authenticator will allow us to manually access your tenant for validation purposes and, if you use PIM, to activate the role. The specific AAGUID is for Patriot's Azure Key Vault that will be used for the scan process.",
    "Save the profile. Known quirk: the portal sometimes shows an error or \"unsaved edits will be discarded\" warning even when the profile saved successfully. Re-open the profile and spot-check the values rather than re-entering them.",
    "Back on Enable and target: add a target → select the Patriot Scan 365 security group → in the profile dropdown choose Patriot SecureScan365 Passkey Profile.",
    "If an \"All users\" target with the default passkey profile was added when you enabled the method and your organization does not otherwise use passkeys, remove it. With only the group target in place, passkeys are available to exactly one account — yours to control — and passkey does not appear as a registration option for anyone else.",
    "Save.",
  ],
  images: [
    { src: "/wizard/e4-n4-passkey-profile-config.png" },
    {
      src: "/wizard/e4-n4-passkey-enable-target.png",
      // This screenshot shows the state the instruction tells them to
      // FIX (the "All users" target still present) — it must be labeled
      // or it reads as the desired end state.
      caption: "Before removing the All users target — if your tenant shows this row, delete it so only the Patriot group remains.",
    },
  ],
  tip: "Pasting the AAGUID: paste it into a plain-text editor first and re-copy it. Rich-text copy/paste can carry invisible characters that make the value fail validation with an unhelpful error.",
  // Pablo captured the warning dialog specifically for THIS hint — it
  // belongs here, not in the step's main image list.
  troubleshooting: {
    text: "The portal sometimes shows an error or \"unsaved edits will be discarded\" warning even when the profile saved. Re-open it and spot-check the values instead of re-entering them.",
    images: [{
      src: "/wizard/e4-n4-unsaved-edits-warning.png",
      caption: "This warning often appears even when the profile saved. Re-open the profile and check the values rather than re-entering them.",
    }],
  },
};

// Graph consent — TWO routes, shared verbatim by E3 and N7. Q-2 was
// withdrawn in the 2026-08-04 sweep (the portal-path sentence cut);
// Pablo's 2026-08-03 answer then CLOSED it outright: there is no Entra
// portal click path for this consent, so the wording now offers exactly
// the two real routes. They are phrased to be true on BOTH paths
// without asserting which one applies: whether Patriot currently holds
// a Global Administrator account is a fact about the tenant, not about
// the chosen path.
const GRAPH_CONSENT_ROUTES = [
  "Consenting to Graph permissions requires a Global Administrator. There are two ways to do it:",
  "Patriot completes it on your behalf. If Patriot currently holds an account with Global Administrator in your tenant, we can grant the consent using the access we already have — no action needed from your team. Worth asking us first: it takes the step off your plate entirely.",
  "A Global Administrator on your team runs the command below.",
];

// The scope list is the reviewed v2 command — Pablo confirmed on the
// review call that it is unchanged now that the Fabric/Hybrid roles are
// dropped (Q-1 resolved).
const GRAPH_CONSENT_POWERSHELL = [
  "On your workstation, open PowerShell 7 and install the Microsoft Graph authentication module (only this one submodule is needed, not the full Graph SDK): Install-Module Microsoft.Graph.Authentication -Scope CurrentUser. Scoped to the current user, this does not require local administrator rights on the machine. Answer Yes to All if prompted about the repository.",
  {
    text: "Run the connection command:",
    copy: {
      label: "Connect-MgGraph command",
      value: "Connect-MgGraph -Scopes \"AccessReview.Read.All\",\"Agreement.Read.All\",\"AuditLog.Read.All\",\"DeviceManagementConfiguration.Read.All\",\"DeviceManagementManagedDevices.Read.All\",\"DeviceManagementRBAC.Read.All\",\"DeviceManagementServiceConfig.Read.All\",\"Directory.Read.All\",\"DirectoryRecommendations.Read.All\",\"EntitlementManagement.Read.All\",\"IdentityRiskEvent.Read.All\",\"IdentityRiskyServicePrincipal.Read.All\",\"IdentityRiskyUser.Read.All\",\"OnPremDirectorySynchronization.Read.All\",\"OrgSettings-AppsAndServices.Read.All\",\"OrgSettings-Forms.Read.All\",\"Policy.Read.All\",\"Policy.Read.ConditionalAccess\",\"PrivilegedAccess.Read.AzureAD\",\"PrivilegedEligibilitySchedule.Read.AzureADGroup\",\"Reports.Read.All\",\"ReportSettings.Read.All\",\"RoleAssignmentSchedule.Read.Directory\",\"RoleEligibilitySchedule.Read.Directory\",\"RoleManagement.Read.All\",\"SecurityIdentitiesHealth.Read.All\",\"SecurityIdentitiesSensors.Read.All\",\"SharePointTenantSettings.Read.All\",\"ThreatHunting.Read.All\",\"UserAuthenticationMethod.Read.All\"",
    },
  },
  "Sign in with your Global Administrator account when prompted.",
  "On the consent screen, review the permissions — note that every scope ends in .Read.All or equivalent read-only form — check Consent on behalf of your organization, and accept.",
  "\"Consent on behalf of your organization\" does not open Microsoft Graph access to everyone in the organization. Administrators can require user assignment on the Microsoft Graph Command Line Tools enterprise application and allow access only to approved accounts or security groups.",
  // Q-2 (Entra-portal consent click path) WITHDRAWN 2026-08-04, not
  // answered: the Patriot-completes-it route already covers customers
  // who will not run PowerShell. Pablo's 2026-08-03 answer (applied
  // 2026-08-06) then CLOSED it outright — no portal path exists; see
  // GRAPH_CONSENT_ROUTES above.
];

// Shared by N8 and E9 — Pablo confirmed the restriction applies on both
// paths, not just new accounts.
const RESTRICT_CLI_STEP = {
  title: "Restrict who can use Microsoft Graph Command Line Tools (recommended)",
  instructions: [
    "The Graph consent makes the Microsoft Graph Command Line Tools service principal visible in your Enterprise applications. This step limits its use to the Patriot group — the same pattern many organizations apply to Exchange Online PowerShell access.",
    "Go to Entra → Enterprise applications and search for Microsoft Graph Command Line Tools.",
    "Open Users and groups and add the Patriot Scan 365 security group.",
    "Open Properties and set Assignment required to Yes, then save.",
  ],
  images: [{ src: "/wizard/e9-n8-graph-cli-restrict.png" }],
  tip: null,
  troubleshooting: null,
};

// Both Conditional Access captures are deliberately cropped so no policy
// name is visible — a customer must never think they should have that
// policy in their tenant — and both carry the same example caption.
const CA_EXAMPLE_CAPTION =
  "Example only — your policy names and settings will differ. What matters is that the Patriot Scan 365 group appears under the policy's excluded users and groups.";

// Shared by the E3 and N7 Graph-consent steps.
const GRAPH_CONSENT_IMAGES = [
  { src: "/wizard/e3-n7-connect-mggraph-command.png" },
  { src: "/wizard/e3-n7-consent-dialog.png" },
];

// "What you can remove" — the post-migration cleanup step (E10 / N12)
// and the SecureScan365 entry-branch section share this copy. The
// wizard step is LOCKED until the first successful scan
// (scan_tool_key === "securescan365"); its job pre-scan is motivational
// — customers see what they get back while doing the work.
export const CLEANUP_COPY = {
  title: "What you can remove",
  lockedNote: "Unlocks after your first successful SecureScan365 scan.",
  introExisting: "Now that SecureScan365 is running, the following can be removed from your tenant:",
  introNew: "If Patriot previously had access to your tenant, the following can now be removed:",
  items: [
    "Any additional Patriot accounts. Only one scan account is needed. If Patriot delivers other services in your tenant, confirm with your Patriot team before removing accounts.",
    "The Microsoft 365 license on the Patriot account. SecureScan365 requires no license — reclaim it.",
    "The legacy service principal, named PatriotAudit- followed by a GUID. It was part of the old collection method and is no longer used.",
    "Global Administrator on the scan account, if not already removed.",
  ],
};

const cleanupStep = (intro) => ({
  title: CLEANUP_COPY.title,
  lockedUntilScan: true,
  instructions: [intro, ...CLEANUP_COPY.items],
  images: null, // none-needed — a removal checklist, no single UI to capture (operator review 2026-08-04)
  tip: null,
  troubleshooting: null,
});

const KEEP_ACTIVE_STEP = {
  title: "Keep the account active",
  instructions: [
    "We recommend leaving the scan account enabled and Global Reader active — whether the role is held through the Patriot Scan 365 group or directly on the account — rather than disabling anything between assessments. The account is read-only and cannot change anything in your tenant.",
    "While the account stays active, these run automatically: setting regression checks on every scan (coming soon); notifications as new settings are added (coming soon); scans between your quarterly reviews, on your schedule.",
    "If the account is disabled between assessments, scans can only run when you re-enable it.",
    // Q-5 ANSWERED 2026-08-04 (operator): the scan engine CANNOT
    // activate a PIM-eligible role — a Patriot engineer activates it
    // from the backend scanner; there is no portal control. The old
    // "self-activation is workable" claim was wrong, not just draft.
    // Gain-framed per the VALUE_COPY doctrine: state the real cost,
    // no ultimatum.
    "On PIM: a Global Reader role held as PIM-eligible cannot be activated by the scan itself, so scans cannot run on demand or on your own schedule while it is held that way. Keeping the role active and permanent is what lets you run a scan whenever you need one. If your policies require PIM, let your Patriot advisor know and we will work through it with you.",
  ],
  images: null, // none-needed — informational recommendation, nothing to capture (operator review 2026-08-04)
  tip: null,
  troubleshooting: null,
};

// ── Step content, keyed by step id across both paths ─────────────────
export const STEP_CONTENT = {
  // PATH 1 — Existing Patriot account
  E1: {
    title: "Confirm the account",
    instructions: [
      "This is the account Patriot has been using for your assessments. We are not asking for new access — we are reducing what this account can do, from Global Administrator to read-only Global Reader.",
      "Confirm the account's UPN and that the account is enabled. Nothing to create.",
      "Once you have gone through the setup and we have tested and validated, we will advise that the Global Admin role can be replaced with Global Reader for all Patriot auditor accounts.",
    ],
    images: null, // none-needed by design (Pablo: "no screenshot") — renders nothing
    tip: null,
    troubleshooting: null,
  },
  E2: GROUP_STEP,
  E3: {
    title: "Consent to read-only Microsoft Graph permissions",
    instructions: [
      "Do this before removing Global Administrator — while a Patriot account still holds that role, we can complete this step without involving your team.",
      ...GRAPH_CONSENT_ROUTES,
      ...GRAPH_CONSENT_POWERSHELL,
      "Every scope is read-only. The account can retrieve settings; it cannot create, modify, or delete anything.",
    ],
    images: GRAPH_CONSENT_IMAGES,
    tip: null,
    troubleshooting: null,
  },
  E4: PASSKEY_STEP,
  // E5 (Confirm Conditional Access) RETIRED 2026-08-01 — Pablo: existing
  // accounts have been signing in for previous assessments, so exclusions
  // are already in place, and account-level exclusions carry over cleanly
  // when the account joins the group and switches auth method (Q-3
  // resolved). The customer-visible symptom is a failed validation, so
  // the content lives in TROUBLESHOOTING_TABLE now. The id is never
  // reused.
  E9: RESTRICT_CLI_STEP,
  E6: {
    // Q-4 closed (Pablo): reused accounts are registered with the
    // account's existing stored credentials — no Temporary Access Pass
    // language belongs on this path. The no-email TAP stance lives on
    // N9, where a TAP is genuinely used.
    title: "Patriot registers the passkey",
    instructions: [
      "Patriot registers the passkey using the account's existing stored credentials, then validates the setup.",
    ],
    images: null, // none-needed by design (Pablo follow-up, 2026-08-02)
    tip: null,
    troubleshooting: null,
  },
  E7: {
    // Rewritten from "remove GA" to the swap it actually is — the
    // existing path never assigned Global Reader anywhere before this.
    title: "Assign Global Reader, then remove Global Administrator",
    instructions: [
      "Once Patriot confirms a successful scan, complete the least-privilege swap.",
      "First, assign the Global Reader role to the Patriot Scan 365 group — assignment type Active, duration Permanent (not \"Eligible\"). If the group was created without \"Microsoft Entra roles can be assigned to the group\", assign Global Reader directly to the scan account instead.",
      "Then remove Global Administrator from the account.",
      "Patriot cannot change roles in your tenant — this step is yours, and it is the one that completes the least-privilege model.",
    ],
    images: null, // none-needed by design (Pablo follow-up, 2026-08-02)
    tip: null,
    troubleshooting: null,
  },
  E8: KEEP_ACTIVE_STEP,
  E10: cleanupStep(CLEANUP_COPY.introExisting),

  // PATH 2 — New account
  N1: {
    title: "Create the scan account",
    instructions: [
      "Create a standard cloud-only user account. This is a regular user object — no mailbox or license is needed, and no password will ever be used with it.",
      "Go to entra.microsoft.com → Users → All users → New user → Create new user.",
      "Recommended name: PatriotScan365 (display name \"Patriot Scan 365\"). If your organization has a naming convention for vendor or service accounts (for example a \"v-\" prefix), feel free to follow it — the name only needs to be recognizable to your team as Patriot's scan account.",
      "We recommend creating the account on your .onmicrosoft.com domain rather than your primary (vanity) domain.",
      "Let Entra auto-generate the password. You will never use it — do not record or share it.",
      "Create the account.",
    ],
    images: [
      { src: "/wizard/n1-create-user-basics.png" },
      { src: "/wizard/n1-user-properties.png" },
    ],
    tip: null,
    troubleshooting: null,
  },
  N2: GROUP_STEP,
  N3: {
    title: "Assign the Global Reader role",
    instructions: [
      "Global Reader gives the account read access to configuration across your tenant with no ability to change anything. Assign the role to the Patriot Scan 365 GROUP as an active, permanent assignment — the assessment runs on a schedule and cannot activate an eligible role.",
      "Assign Global Reader to the Patriot Scan 365 group. Assignment type: Active, duration Permanent (not \"Eligible\"). Add a justification note such as \"Patriot SecureShield365 assessment access.\"",
      "If the group was created without \"Microsoft Entra roles can be assigned to the group\", this does not block you: assign Global Reader directly to the PatriotScan365 account instead (open the account → Assigned roles → Add assignment).",
      "Assign, then refresh the blade and confirm the role appears under active assignments.",
    ],
    images: null, // none-needed — mirrors the E7 Global Reader call (Pablo follow-up 2026-08-02; operator review 2026-08-04)
    tip: null,
    troubleshooting: null,
  },
  N4: PASSKEY_STEP,
  N5: {
    title: "Enable Temporary Access Pass (scoped to the Patriot group)",
    instructions: [
      "A Temporary Access Pass (TAP) is a short-lived, admin-issued credential used exactly once in this process: it lets Patriot register the passkey without a password ever existing. Like the passkey method, it is scoped to the Patriot security group only. Note: if you have TAP enabled for \"All users\" you can skip this step.",
      "Still in Authentication methods → Policies, select Temporary Access Pass and enable it.",
      "Under Enable and target, choose Select groups and add the Patriot Scan 365 group. Note the targeting is one or the other — selecting a group replaces \"All users.\"",
      "Leave the configuration defaults as they are and save. Verify the policy now shows the single group as its target.",
      "To confirm scoping worked: open any other user's Authentication methods → Add authentication method — Temporary Access Pass should not appear in their dropdown.",
    ],
    images: [{ src: "/wizard/n5-tap-enable-target.png" }],
    tip: "TAP is broadly useful beyond this setup — it is the clean way to let a user who lost their phone re-register MFA without help-desk password resets. Only administrators can issue one; end users cannot generate a TAP for themselves.",
    troubleshooting: null,
  },
  N6: {
    title: "Review Conditional Access policies",
    instructions: [
      "The scan account is a regular user object, so every Conditional Access policy in your tenant applies to it. Policies that commonly block the passkey registration or subsequent sign-ins include: restrictions on registering security information, device-compliance or hybrid-join requirements, trusted-location/geo restrictions, and BYOD-style block policies.",
      "Review your CA policies and add the Patriot Scan 365 security group (or the account directly) as an exclusion on any policy that would block: (a) registering security info, or (b) signing in without a compliant/managed device or from outside your network locations.",
      "If your organization requires change documentation, this is a good point to raise the change record — the exclusions are narrow (one group, one account) and easy to describe.",
      "Allow changes 5–15 minutes to take effect before testing. Policy edits are not always instant, and a change that looks saved may not be enforced yet.",
    ],
    images: [{ src: "/wizard/n6-ca-exclusion.png", caption: CA_EXAMPLE_CAPTION }],
    tip: null,
    troubleshooting: "You don't have to get this perfect in advance. During the access call, Patriot will attempt the passkey registration; if a policy blocks it, the failure shows up immediately in the scan account's sign-in logs with the exact policy named, and the exclusion can be added on the spot.",
  },
  N7: {
    title: "Consent to read-only Microsoft Graph permissions",
    instructions: [
      "This is the one step that requires a Global Administrator — only a Global Admin can consent to Graph API permissions. Every permission in the consent is read-only: the account can retrieve settings but cannot create, modify, or delete anything.",
      ...GRAPH_CONSENT_ROUTES,
      ...GRAPH_CONSENT_POWERSHELL,
      "The next step restricts who can use that service principal to the single Patriot group.",
    ],
    images: GRAPH_CONSENT_IMAGES,
    tip: null,
    troubleshooting: "If the pasted command fails oddly, paste it through a plain-text editor first — rich-text copy from Teams or email can carry hidden characters.",
  },
  N8: RESTRICT_CLI_STEP,
  N9: {
    title: "Issue the Temporary Access Pass — on a short call with Patriot",
    instructions: [
      "This step happens live on the scheduled call, because the Temporary Access Pass expires one hour after it is created. If any earlier step gave you trouble, bring it to this call — we will work through it together first.",
      "The TAP is never collected through this portal or by email — it is a full authentication credential.",
      "On the call: open the PatriotScan365 account → Authentication methods → Add authentication method → Temporary Access Pass.",
      "Keep the default one-hour duration. Set one-time use to No so registration can be retried within the hour if a policy or propagation delay interrupts the first attempt.",
      "Create the TAP and share the TAP value and the account's UPN with your Patriot contact through the secure channel agreed on the call.",
    ],
    images: null, // none-needed by design (Pablo follow-up, 2026-08-02)
    tip: null,
    troubleshooting: null,
  },
  N10: {
    title: "Patriot registers the passkey and validates readiness",
    instructions: [
      "This step is performed by Patriot. Using the UPN and TAP, we will register the passkey and validate the setup together. The passkey is generated and stored in an Azure Key Vault inside Patriot's dedicated, access-restricted environment. From this point forward the TAP is no longer used and no password exists for the account.",
      "Patriot then runs automated readiness checks that verify the pieces from the earlier steps: the Global Reader role and the Graph permission consent. If anything is missing, the check names it specifically and we fix it together on the call.",
    ],
    images: null, // none-needed by design (Pablo follow-up, 2026-08-02)
    tip: null,
    troubleshooting: null,
  },
  N11: KEEP_ACTIVE_STEP,
  N12: cleanupStep(CLEANUP_COPY.introNew),
};

// Every step carries an images slot with a three-state meaning:
//   [{src, caption?}, ...] — screenshots delivered (frontend/public/wizard/)
//   []                     — screenshot PENDING: render the placeholder box
//   null                   — none NEEDED by design: render nothing
// NO normalizer, deliberately (operator decision 2026-08-04): every step
// declares images EXPLICITLY — a populated array (figures), [] (pending
// screenshot → placeholder box), or null (none needed). The old
// absent-key→[] default silently rendered "Screenshot placeholder" boxes
// on steps that never needed one (E8/E10/N3/N11/N12). An absent key now
// fails the every-step-declares-images test instead of shipping a
// placeholder.

// ── Shared reference content (reachable from both paths + entry) ─────
// Roles-and-access table, verbatim from the reviewed v2 doc.
export const ROLES_ACCESS_TABLE = [
  { "Task": "Create the scan account and security group", "Least-privileged role(s)": "User Administrator (or higher)" },
  { "Task": "Assign the Global Reader role", "Least-privileged role(s)": "Privileged Role Administrator or Global Administrator" },
  { "Task": "Create/modify the passkey profile and enable TAP", "Least-privileged role(s)": "Authentication Policy Administrator, Privileged Role Administrator, or Global Administrator" },
  { "Task": "Issue the Temporary Access Pass", "Least-privileged role(s)": "Authentication Administrator (Privileged Authentication Administrator for admin accounts) or higher" },
  { "Task": "Consent to Graph API permissions", "Least-privileged role(s)": "One of your Global Administrators — required once; only a Global Admin can grant this consent" },
  { "Task": "Edit Conditional Access policy exclusions", "Least-privileged role(s)": "Conditional Access Administrator, Security Administrator, or Global Administrator" },
];

// Second table in the Roles & access reference: not who does the setup,
// but what Patriot ends up holding. Draft-flagged pending sign-off
// (PATRIOT_ACCESS_TABLE row in MOCK_REGISTRY).
export const PATRIOT_ACCESS_TABLE_TITLE = "What Patriot's access consists of";
export const PATRIOT_ACCESS_TABLE = [
  { "Item": "Directory role", "Detail": "Global Reader — one scan account, read-only" },
  // Reworded 2026-08-04 with the Q-5 answer — the old "preference, not a
  // technical requirement" line contradicted the new PIM note.
  { "Item": "How the role is held", "Detail": "Global Reader, active and permanent. PIM-eligible is possible where your policies require it, but the role cannot be activated by the scan itself, so on-demand and scheduled scans are not available while it is held that way." },
  { "Item": "Graph permissions", "Detail": "Read-only scopes consented to Microsoft Graph Command Line Tools, a Microsoft first-party app. Delegated — exercised only when the scan account signs in." },
  { "Item": "Application identity", "Detail": "None. No app registration, no service principal, no client secret or certificate." },
  { "Item": "Authentication", "Detail": "Device-bound passkey held in Patriot's Azure Key Vault" },
  { "Item": "Microsoft 365 license", "Detail": "Not required" },
  { "Item": "Microsoft Fabric license", "Detail": "Not required" },
  { "Item": "Global Administrator", "Detail": "Never held by the scan account. Not requested, not granted, not retained." },
  { "Item": "Other Patriot services", "Detail": "No other Patriot account requires Global Administrator. Patriot's other services use least-privileged roles. If a specific engagement needs elevated access — incident response, or changes you ask us to make — the engineer requests it at the time." },
  { "Item": "Fabric Administrator / Hybrid Identity Administrator", "Detail": "Not requested" },
  { "Item": "Number of Patriot accounts", "Detail": "One" },
  { "Item": "How to revoke", "Detail": "Remove from the group, disable or delete the account, or delete the registered passkey — any one ends access immediately" },
];

export const ROLES_ACCESS_PIM_NOTE =
  "If you use PIM (Privileged Identity Management): activate your elevated role for a long enough window to complete the work — at least two hours if you are doing everything in one sitting. An elevation that expires mid-session causes vague, unhelpful save errors in the Entra portal (settings appear to fail for no reason). If saves suddenly start failing, check your PIM activation before troubleshooting anything else.";

export const SECURITY_FAQ_INTRO =
  "These are the questions customers raise most often during setup. Your security team may want this page even if they skip the rest.";

// Security FAQ — the reviewed v2 questions (step references neutralized
// to step names since numbering is now per-path), the existing-account
// additions, and the customer questions Pablo answered 2026-08-03.
//
// COVERAGE WORDING: Global Reader delivers "everything the
// SecureShield365 assessment evaluates" (Pablo confirmed). Never a bare
// "full assessment", and never a comparison of Global Reader against
// Global Administrator coverage.
//
// Two earlier entries were MERGED rather than duplicated by the Pablo
// round: "Where does the passkey live?" became the Key Vault entry, and
// "Will removing Global Administrator break anything else Patriot
// does?" (Q-7) became the other-Patriot-accounts entry — the operator's
// 2026-08-04 "set up separately" scoping is preserved inside it.
export const SECURITY_FAQ = [
  {
    q: "Why not just give Patriot a Global Admin account?",
    a: "You shouldn't, and we don't want one. Global Reader plus read-only Graph scopes covers everything the SecureShield365 assessment evaluates, with zero ability to change anything.",
  },
  {
    q: "Can this account modify our tenant?",
    a: "No. Global Reader is read-only by design, and every Graph permission consented in the Graph consent step is a read-only scope. There are no write, update, or delete permissions anywhere in this setup.",
  },
  {
    // Merged from the earlier "Where does the passkey live?" entry — the
    // answer below covers where it lives and why. No claims about
    // in-vault generation, non-exportability, or HSM backing: unverified.
    q: "Why is a Key Vault needed in this case?",
    a: "A passkey is a credential and it has to be stored somewhere. Keeping it in an Azure Key Vault inside Patriot's dedicated, access-restricted environment means it is not on an engineer's laptop, in a password manager entry, or in a file that could be copied or forwarded. Access to that environment is limited to a small number of Patriot personnel and protected by Conditional Access. It is also what allows a scheduled scan to authenticate with no password and no stored application secret.",
  },
  {
    q: "Does the passkey expire?",
    a: "No — and that's why control stays with you: disable the account, remove it from the group, or delete the registered passkey and access ends immediately.",
  },
  {
    q: "Doesn't \"consent on behalf of the organization\" open Graph to everyone?",
    a: "No. It authorizes the service principal for those read-only scopes; the Assignment-required restriction on the Microsoft Graph Command Line Tools application limits who can use it to the one Patriot group.",
  },
  {
    q: "Do our Conditional Access policies still apply to this account?",
    a: "Yes — it is a regular user object and every CA policy applies. Any exclusions you granted are narrow and visible in your policy configuration.",
  },
  {
    q: "Can we see what Patriot's account is doing?",
    a: "Yes. All sign-ins appear in the account's sign-in logs, and the granted permissions (and who granted them) are visible on the Microsoft Graph Command Line Tools enterprise application.",
  },
  {
    q: "How do we revoke access?",
    a: "Any of: disable or delete the account, remove it from the security group, delete the registered passkey, or remove the enterprise-application assignment. Each one independently stops access.",
  },
  {
    q: "Why is Patriot reusing an existing account rather than creating a new one?",
    a: "It is faster and lower-risk. The account already exists, is already excluded from policies that would block setup, and is already known to your team. We are reducing its privilege, not adding any.",
  },
  {
    q: "Graph permissions are usually applied to a service principal or app registration, but you are moving away from a service principal. Where are these permissions applied?",
    a: "The consent is granted to Microsoft Graph Command Line Tools, a Microsoft first-party application that already exists in your tenant. Patriot does not create an app registration or a service principal, and there is no client secret or certificate anywhere in this setup. The permissions are delegated rather than application permissions, so they are only exercised when the scan account itself signs in with its passkey — nothing runs as a standalone application identity. You can review exactly what was consented, and who granted it, under Enterprise applications, Microsoft Graph Command Line Tools. A later step restricts use of that application to the Patriot group.",
  },
  {
    q: "Is Windows Hello necessary? Could we allow only Microsoft Authenticator and the AAGUID for your Key Vault?",
    a: "All three are used. The Key Vault AAGUID is what the automated scan authenticates with. Windows Hello and Microsoft Authenticator allow a Patriot engineer to sign in manually when a setting needs validating or a licence needs checking. Restricting the list to the Key Vault AAGUID alone means those manual checks are not possible, and problems that would take minutes on a call take longer to resolve.",
  },
  {
    q: "Is this setup recommended by Patriot for passkey enrolment generally? I have not seen it in our assessments or in Microsoft's documentation.",
    a: "It is deliberately different from standard passkey enrolment and should not be read as a recommendation for your users. Standard guidance assumes a person with their own device, and our assessment recommendations reflect that. This is a service account that must authenticate on a schedule with nobody present. The realistic options for that are a password, a stored application secret, or a passkey held in a protected vault, and the last is the only one that is phishing-resistant and leaves nothing reusable behind. The mechanism itself is standard — AAGUID-scoped passkey profiles are a built-in Microsoft Entra capability. What is unusual is applying it to a service account rather than to people, and that is deliberate.",
  },
  {
    // Answer aligned to the Q-5 resolution (operator, 2026-08-04): the
    // scan cannot activate a PIM-eligible role — a Patriot engineer
    // does. No failure assertion, matching KEEP_ACTIVE and the
    // Patriot-access table's "How the role is held" row.
    q: "Are we still implementing PIM? Should I enable it?",
    a: "PIM is not required and whether you use it is your decision. What matters is that Global Reader is active when a scan runs. A role held as PIM-eligible cannot be activated by the scan itself, so on-demand and scheduled scans are not available while it is held that way — keeping the role active and permanent is what lets a scan run whenever you need one. If your policies require PIM, let your Patriot advisor know and we will work through it with you.",
  },
  {
    // Merged from "Will removing Global Administrator break anything
    // else Patriot does?" — Q-7 answered (Pablo, 2026-08-03), keeping
    // the operator's 2026-08-04 scoping: other services are set up
    // separately from this walkthrough.
    q: "Will other Patriot accounts still need access to our environment?",
    a: "No other Patriot account requires Global Administrator, and Patriot's other services use least-privileged roles set up separately from this walkthrough. If a specific engagement needs elevated access — incident response, or changes you ask us to make — the engineer requests it at the time. Only one account is needed for SecureScan365; see \"What you can remove\" for what can be cleaned up once your first scan succeeds.",
  },
  {
    q: "We assigned Microsoft 365 licences to the Patriot accounts. Do those need to be maintained?",
    a: "No. SecureScan365 requires no Microsoft 365 licence on the scan account, and no Microsoft Fabric licence either. The legacy collection method needed a licensed account; this one does not. Once your first scan completes successfully those licences can be released back to your pool.",
  },
  {
    q: "There was a service principal named PatriotAudit- followed by a GUID. Did that go away with the change in collection process?",
    a: "Yes. The service principal belonged to the legacy collection method, which used an application identity with its own credentials. SecureScan365 does not use one. If it still exists in your tenant it can be deleted once your first scan completes successfully.",
  },
];

// Troubleshooting — the reviewed v2 table minus the two license rows
// ("License assignment fails" / "Fabric (Free) license not listed"),
// which are unreachable now that no license is required. The v2 doc
// carries 9 symptoms (not the 10 the build note said) — 7 remain.
export const TROUBLESHOOTING_TABLE = [
  {
    "Symptom": "Settings won't save in the Entra portal; vague or generic errors",
    "Likely cause and fix": "Check whether your PIM elevation expired — the portal gives no warning when it does. Re-activate your role and retry. If errors persist on a passkey profile specifically, delete and recreate the profile.",
  },
  {
    "Symptom": "Passkey profile shows an error or \"unsaved edits\" warning on save",
    "Likely cause and fix": "Known portal quirk — the save often succeeded anyway. Re-open the profile and verify the values before re-entering anything.",
  },
  {
    "Symptom": "Can't add a passkey profile / Configure tab unavailable",
    "Likely cause and fix": "The Passkey (FIDO2) method must be enabled and saved once before profiles can be added. If your tenant has Security Defaults enabled (common in brand-new tenants), they must be disabled — in favor of Conditional Access policies — before these methods can be managed.",
  },
  {
    "Symptom": "Patriot's registration or sign-in is blocked",
    "Likely cause and fix": "Open the scan account → Sign-in logs → select the failed/interrupted event → Conditional Access tab. The blocking policy shows a \"Failure\" result — click through (via the read-only banner) to edit it and add the Patriot group as an exclusion. Allow 5–15 minutes for the change to take effect, and expect the sign-in logs themselves to lag by a few minutes.",
  },
  {
    "Symptom": "Exclusion added but registration still fails",
    "Likely cause and fix": "Usually propagation delay. Verify the exclusion saved (re-open the policy), wait 10 minutes, and have Patriot retry — the TAP allows multiple attempts within its one-hour window.",
  },
  {
    "Symptom": "AAGUID fails validation when pasted",
    "Likely cause and fix": "Hidden characters from rich-text copy/paste. Paste into a plain-text editor, re-copy, and re-enter.",
  },
  {
    "Symptom": "Passkey or TAP options aren't visible or manageable under Authentication methods",
    "Likely cause and fix": "Your tenant may still be managed under the legacy per-user MFA settings. Stop here and contact your Patriot advisor before making changes — we will walk through it with you.",
  },
  // ── Rows added from Pablo's review (2026-08-01). The first is the
  // retired E5 Conditional Access step, rekeyed on the symptom the
  // customer actually sees. All five are draft-flagged in MOCK_REGISTRY
  // (DRAFT_TROUBLESHOOTING_ADDITIONS) pending Pablo's sign-off. ──
  {
    "Symptom": "Validation reports a blocked sign-in",
    "Likely cause and fix": "A Conditional Access policy is blocking the scan account. Open the account's Sign-in logs and select the failed event — the failure names the blocking policy. Add the Patriot Scan 365 group as an exclusion on that policy, and allow 5–15 minutes for the change to take effect.",
    // Row-attached illustration (not a step image): the exclusion panel,
    // cropped so no policy name shows.
    image: { src: "/wizard/ca-exclusion-example.png", caption: CA_EXAMPLE_CAPTION },
  },
  {
    "Symptom": "Passkey registration fails against Microsoft's default passkey profile",
    "Likely cause and fix": "Registration must go through the dedicated Patriot SecureScan365 Passkey Profile, not Microsoft's default profile. Confirm the profile from the passkey step exists and is targeted at the Patriot Scan 365 group, then retry.",
  },
  {
    "Symptom": "Security Reader assigned instead of Global Reader",
    "Likely cause and fix": "The two roles look similar, but Security Reader does not cover the reads the assessment needs. Remove Security Reader and assign Global Reader — to the Patriot Scan 365 group, or directly to the account if the group was created without role assignment enabled.",
  },
  {
    "Symptom": "Scan sign-ins blocked by stale named-location IPs",
    "Likely cause and fix": "A Conditional Access named location or trusted-IP list containing out-of-date addresses can block the scan. Check your named locations against Patriot's current addresses — contact your Patriot advisor for the current list — and update stale entries.",
  },
  {
    "Symptom": "Patriot cannot sign in to validate the setup",
    "Likely cause and fix": "Manual validation may require a Temporary Access Pass. Issue a TAP on the scan account (default one-hour duration, one-time use set to No) and share it with your Patriot contact through the secure channel agreed with them.",
  },
];

// Approved value-story copy for the upgrade-section entry state (webinar
// prep, 2026-07-31). Use close to verbatim — do not embellish, do not add
// claims. The comingSoon note is deliberately gain-framed ("run
// automatically while the scan account stays active") and must never be
// rephrased as loss framing ("you lose X if you disable the account").
export const VALUE_COPY = {
  removes: {
    title: "What SecureScan365 removes",
    items: [
      "Global Administrator access",
      "A Microsoft 365 licensed account",
      "The service principal the legacy process required",
    ],
  },
  today: {
    title: "What you get today",
    items: [
      "Global Reader only, read-only, revocable at any time",
      "Over 1,000 security settings assessed, with more added regularly",
      "Scans complete in minutes rather than days of coordination",
      "Run your own scan any time, up to once every 24 hours",
    ],
  },
  comingSoon: {
    // Renders on the LEGACY entry branch only (with the "Not yet
    // available" badge) — never on the already-migrated branch.
    title: "Coming soon to SecureScan365 *",
    items: [
      "Setting regression checks on every scan",
      "Single-setting rescans",
      "Notifications as new settings are added",
    ],
    note: "* These run automatically while the scan account stays active.",
  },
};

export const COMPLETION_SUBJECT = "SecureScan365 Prerequisites — Phase A complete";
export const WIZARD_COMPLETION_SUBJECT_PREFIXES = [
  COMPLETION_SUBJECT,
  "SecureScan365 upgrade — Phase A complete", // legacy v1
];

function isOpenWizardTicket(ticket = {}) {
  const subject = String(ticket.subject || "");
  const subjectMatches = WIZARD_COMPLETION_SUBJECT_PREFIXES.some(
    prefix => subject.startsWith(prefix)
  );
  // Freshservice 4=Resolved / 5=Closed are inactive — same rule as
  // SupportPage and SupportSnapshot in SecureShield365.js.
  return subjectMatches && ticket.status !== 4 && ticket.status !== 5;
}

export function findOpenWizardTicket(tickets = []) {
  return (Array.isArray(tickets) ? tickets : []).find(isOpenWizardTicket) || null;
}

export function hasOpenWizardTicket(tickets = []) {
  return !!findOpenWizardTicket(tickets);
}

function escapeTicketHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export function needHelpTicketBody({
  reasonKey, customerLabel, contactEmail, scanToolKey,
  stepId, stepLabel, pathKey, checklist, freeText,
}) {
  const reasonLabel =
    HELP_REASONS.find(reason => reason.key === reasonKey)?.label || reasonKey;
  const stepRow = stepId
    ? `${escapeTicketHtml(stepId)} — ${escapeTicketHtml(stepLabel || "")}`
    : "Not started";
  const checklistRows = pathKey
    ? pathSteps(pathKey).map(key =>
        `<tr><td><b>${escapeTicketHtml(key)}</b></td><td>${escapeTicketHtml(checklist?.[key] || "pending")}</td></tr>`
      ).join("")
    : "";
  return `
<h3>SecureScan365 Prerequisites — help requested</h3>
<table>
  <tr><td><b>Reason:</b></td><td>${escapeTicketHtml(reasonLabel)} (${escapeTicketHtml(reasonKey)})</td></tr>
  <tr><td><b>Customer:</b></td><td>${escapeTicketHtml(customerLabel || "")}</td></tr>
  <tr><td><b>Contact:</b></td><td>${escapeTicketHtml(contactEmail || "")}</td></tr>
  <tr><td><b>Current scan tool:</b></td><td>${escapeTicketHtml(scanToolKey || "Unclassified")}</td></tr>
  <tr><td><b>Step reached:</b></td><td>${stepRow}</td></tr>
  ${pathKey ? `<tr><td><b>Path:</b></td><td>${escapeTicketHtml(WIZARD_PATHS[pathKey]?.label || pathKey)}</td></tr>` : ""}
  ${checklistRows}
</table>
<h4>Description</h4>
<p>${escapeTicketHtml(freeText || "No additional description provided.")}</p>
<hr>
<p><i>Submitted via the SecureShield365 Company Profile</i></p>`;
}

// Best-effort duplicate guard. Ticket-read failure deliberately falls through
// to createTicket so a read outage never blocks Phase-A completion.
export async function fileCompletionWithGuard({
  fetchTickets,
  createTicket,
  slug,
  payload,
}) {
  let tickets = [];
  try {
    const response = await fetchTickets(slug);
    tickets = Array.isArray(response) ? response : (response?.tickets || []);
  } catch (_) {
    tickets = [];
  }
  const existing = findOpenWizardTicket(tickets);
  if (existing) return { filed: false, existing };
  const ticket = await createTicket(payload);
  return { filed: true, ticket };
}

// One row per open item. Clearing an entry means supplying its real value
// and flipping only that row to draft:false. The DRAFT_Q rows are the
// spec's open questions (v3 draft, "Open questions" table — Q-6 does not
// exist in the spec); their markers appear inline in STEP_CONTENT /
// SECURITY_FAQ. None are answered here.
export const MOCK_REGISTRY = [
  // Final four sign-offs cleared 2026-08-05 (operator): copy unchanged,
  // flags only — the content itself was already final-form.
  { key: "ENTRY_COPY", owner: "James/Amber/Joe", draft: false },
  { key: "WIZARD_COPY", owner: "James/Amber/Joe", draft: false },
  // Q-1 resolved on Pablo's review call: scope list unchanged after the
  // Fabric/Hybrid role drops. Q-3 resolved the same call: existing CA
  // exclusions carry over cleanly (the E5 step is retired).
  { key: "DRAFT_Q1_GRAPH_SCOPES_AND_COUNT", owner: "Nathan", draft: false },
  // Q-2 WITHDRAWN 2026-08-04 (operator decision): the Entra-portal
  // consent sentence was cut, not answered — route (a) covers non-
  // PowerShell customers. Row removed, not cleared.
  { key: "DRAFT_Q3_CA_EXCLUSION_CARRYOVER", owner: "Pablo", draft: false },
  // Q-4 closed (reused accounts use stored credentials, no TAP) and the
  // Windows Hello question closed (all three AAGUIDs required) — Pablo
  // follow-ups 2026-08-02; rows removed.
  // Q-5 ANSWERED 2026-08-04 (operator): engine cannot activate PIM —
  // Patriot engineer activates from the backend scanner; KEEP_ACTIVE
  // note and PATRIOT_ACCESS_TABLE "How the role is held" both reworded.
  { key: "DRAFT_Q5_PIM_ACTIVATION", owner: "Nathan", draft: false },
  // Q-7 ANSWERED 2026-08-04 (operator): Global Reader is all
  // SecureScan365 needs; FAQ answer scoped to this walkthrough only.
  { key: "DRAFT_Q7_GA_STILL_NEEDED_ELSEWHERE", owner: "Pablo", draft: false },
  // Q-8 DECIDED 2026-08-04 (operator): recommend the name, allow
  // customer convention — copy confirmed as written, markers removed.
  { key: "DRAFT_Q8_NAMING_CONVENTION", owner: "James", draft: false },
  // DECIDED 2026-08-04 (operator): IP values are NOT published — the
  // row points customers at their Patriot advisor for the current list.
  { key: "DRAFT_NAMED_LOCATION_IPS", owner: "Pablo/Nathan", draft: false },
  // Pablo's five 2026-08-01 troubleshooting rows: reviewed and approved
  // by the operator 2026-08-05.
  { key: "DRAFT_TROUBLESHOOTING_ADDITIONS", owner: "Pablo", draft: false },
  { key: "PATRIOT_ACCESS_TABLE", owner: "James/Pablo", draft: false },
  // Screenshots delivered by Pablo 2026-08-02 (11 PNGs in
  // frontend/public/wizard/) — real values, row cleared.
  { key: "STEP_IMAGES", owner: "Pablo", draft: false },
];

// The Prerequisites Guide PDF button was removed 2026-08-01 — the wizard
// IS the prerequisites guide. The Security Architecture (PDF) stub was
// removed 2026-08-04 (operator decision: no plan to ship it; the
// 2026-08-01 "stays" call from the Pablo review is superseded).

export const HAS_DRAFT_MOCKS = MOCK_REGISTRY.some(entry => entry.draft);
